import React, { useState } from "react";
import {
	Avatar,
	Box,
	Button,
	Container,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Stack,
	TextField,
	Typography,
	Slide,
	Collapse,
	useMediaQuery,
	useTheme,
	CircularProgress,
} from "@mui/material";
import { PhotoCamera, Edit, Save, Cancel, Lock } from "@mui/icons-material";
import useAuth from "../../hooks/useAuth";
import axios from "../../api/axios";

function ProfileSection({ title, children }) {
	return (
		<Paper
			elevation={3}
			sx={{
				p: 3,
				borderRadius: 2,
				mb: 3,
				transition: "all 0.3s ease-in-out",
				"&:hover": {
					boxShadow: 6,
				},
			}}>
			<Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: "bold" }}>
				{title}
			</Typography>
			{children}
		</Paper>
	);
}

export default function AdminProfileSetting() {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { auth } = useAuth();

	const profileUpdateEndpoint = "/update-admin-profile"; // your profile update API
	const passwordUpdateEndpoint = "/update-admin-password"; // your password update API

	// Profile state
	const [profile, setProfile] = useState({
		name: typeof auth?.fullName === "undefined" ? "..." : auth?.fullName,
		email: typeof auth?.email === "undefined" ? "..." : auth?.email,
		phone: "",
		address: "",
		avatar: "", // URL or base64 string
	});

	// Editable states
	const [editMode, setEditMode] = useState(false);
	const [saving, setSaving] = useState(false);

	// Password change state
	const [passwords, setPasswords] = useState({
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});
	const [showPasswordFields, setShowPasswordFields] = useState(false);
	const [passwordSaving, setPasswordSaving] = useState(false);

	// Handle profile input changes
	const handleProfileChange = (field) => (e) => {
		setProfile((prev) => ({ ...prev, [field]: e.target.value }));
	};

	const handleAvatarChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = () => {
				setProfile((prev) => ({ ...prev, avatar: reader.result }));
			};
			reader.readAsDataURL(file);
		}
	};

	const handleEditToggle = () => {
		setEditMode((prev) => !prev);
	};

	const handleCancelEdit = () => {
		setEditMode(false);
		// Optionally, reset profile to original data if needed
	};

	// Function to update profile via API
	const handleSaveProfile = async () => {
		setSaving(true);
		try {
			const response = await axios.put(profileUpdateEndpoint, profile);
			// Optionally, handle response
			// e.g., update auth context if needed
			console.log("Profile updated:", response.data);
		} catch (error) {
			console.error("Failed to update profile:", error);
		} finally {
			setSaving(false);
			setEditMode(false);
		}
	};

	// Handle password input changes
	const handlePasswordChange = (field) => (e) => {
		setPasswords((prev) => ({ ...prev, [field]: e.target.value }));
	};

	// Function to change password via API
	const handlePasswordSave = async () => {
		setPasswordSaving(true);
		try {
			const response = await axios.patch(passwordUpdateEndpoint, {
				currentPassword: passwords.currentPassword,
				newPassword: passwords.newPassword,
				confirmPassword: passwords.confirmPassword,
				email: auth?.email,
			});
			// Handle success
			console.log("Password changed:", response.data);
		} catch (error) {
			console.error("Failed to update password:", error);
		} finally {
			setPasswordSaving(false);
			setShowPasswordFields(false);
			setPasswords({
				currentPassword: "",
				newPassword: "",
				confirmPassword: "",
			});
		}
	};

	return (
		<Container maxWidth="md" sx={{ py: 4 }}>
			{/* Profile Overview */}
			<ProfileSection title="Profile Overview">
				<Stack alignItems="center" spacing={2}>
					<Avatar
						src={profile.avatar || "/default-avatar.png"}
						sx={{ width: 100, height: 100, cursor: "pointer" }}
						alt="Avatar"
					/>
					<Button
						variant="outlined"
						startIcon={<PhotoCamera />}
						component="label">
						Change Photo
						<input
							hidden
							accept="image/*"
							type="file"
							onChange={handleAvatarChange}
						/>
					</Button>
					<Typography variant="h6">{profile.name}</Typography>
					<Typography variant="body1" color="textSecondary">
						{profile.email}
					</Typography>
				</Stack>
			</ProfileSection>

			{/* Personal Details */}
			<ProfileSection title="Personal Details">
				<Slide direction="up" in={editMode} mountOnEnter unmountOnExit>
					<Box component="form" noValidate autoComplete="off">
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Full Name"
									InputProps={{
										readOnly: true,
									}}
									fullWidth
									value={profile.name}
									onChange={handleProfileChange("name")}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									label="Email"
									fullWidth
									type="email"
									InputProps={{
										readOnly: true,
									}}
									value={profile.email}
									onChange={handleProfileChange("email")}
								/>
							</Grid>
							{/* Additional fields like phone and address can be added here */}
						</Grid>
					</Box>
				</Slide>
				<Collapse in={!editMode} timeout={300}>
					<Box sx={{ mt: 2, display: "flex", gap: 2 }}>
						<Button
							variant="contained"
							startIcon={<Edit />}
							onClick={handleEditToggle}>
							Edit
						</Button>
						{editMode && (
							<>
								<Button
									variant="outlined"
									color="primary"
									startIcon={<Save />}
									onClick={handleSaveProfile}
									disabled={saving}>
									{saving ? <CircularProgress size={20} /> : "Save"}
								</Button>
								<Button
									variant="outlined"
									startIcon={<Cancel />}
									onClick={handleCancelEdit}>
									Cancel
								</Button>
							</>
						)}
					</Box>
				</Collapse>
			</ProfileSection>

			{/* Security Settings */}
			<ProfileSection title="Security Settings">
				<Button
					variant="outlined"
					startIcon={<Lock />}
					onClick={() => setShowPasswordFields((prev) => !prev)}
					sx={{ mb: 2 }}>
					{showPasswordFields ? "Hide Password Fields" : "Change Password"}
				</Button>
				<Collapse in={showPasswordFields} timeout={300}>
					<Box
						component="form"
						noValidate
						autoComplete="off"
						sx={{
							mt: 2,
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}>
						<TextField
							label="Current Password"
							type="password"
							value={passwords.currentPassword}
							onChange={handlePasswordChange("currentPassword")}
							fullWidth
						/>
						<TextField
							label="New Password"
							type="password"
							value={passwords.newPassword}
							onChange={handlePasswordChange("newPassword")}
							fullWidth
						/>
						<TextField
							label="Confirm New Password"
							type="password"
							value={passwords.confirmPassword}
							onChange={handlePasswordChange("confirmPassword")}
							fullWidth
						/>
						<Button
							variant="contained"
							color="primary"
							startIcon={
								passwordSaving ? <CircularProgress size={20} /> : <Save />
							}
							onClick={handlePasswordSave}
							disabled={passwordSaving}>
							{passwordSaving ? "Saving..." : "Save Password"}
						</Button>
					</Box>
				</Collapse>
			</ProfileSection>
		</Container>
	);
}
